/**
 * @file Browser-compatible encryption utilities for React
 */

import CryptoJS from "crypto-js";
import awsConfig from "../../awsConfig";

interface EncryptionParams {
  key: string;
  iv: string;
}

/**
 * Get encryption parameters from config
 */
export function getEncryptionParameters(): EncryptionParams {
  if (!awsConfig.encryption || !awsConfig.encryption.KEY || !awsConfig.encryption.IV) {
    throw new Error("Failed to retrieve encryption parameters from config");
  }

  return {
    key: awsConfig.encryption.KEY,
    iv: awsConfig.encryption.IV,
  };
}

/**
 * Encrypts a string using AES-256-CBC
 */
export function encrypt(text: string): string {
  try {
    const { key, iv } = getEncryptionParameters();

    // CryptoJS requires WordArray objects for key and IV
    const keyWords = CryptoJS.enc.Hex.parse(key);
    const ivWords = CryptoJS.enc.Hex.parse(iv);

    // Encrypt
    const encrypted = CryptoJS.AES.encrypt(text, keyWords, {
      iv: ivWords,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return encrypted.toString();
  } catch {
    throw new Error("Failed to encrypt the provided string");
  }
}

/**
 * Decrypts an encrypted string
 */
export function decrypt(encryptedText: string): string {
  try {
    const { key, iv } = getEncryptionParameters();

    // CryptoJS requires WordArray objects for key and IV
    const keyWords = CryptoJS.enc.Hex.parse(key);
    const ivWords = CryptoJS.enc.Hex.parse(iv);

    // Decrypt
    const decrypted = CryptoJS.AES.decrypt(encryptedText, keyWords, {
      iv: ivWords,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch {
    throw new Error("Failed to decrypt the provided string");
  }
}

/**
 * Encrypts an array of strings
 */
export function encryptBatch(texts: string[]): string[] {
  if (!Array.isArray(texts)) {
    throw new Error("Input must be an array of strings");
  }

  try {
    const { key, iv } = getEncryptionParameters();
    const keyWords = CryptoJS.enc.Hex.parse(key);
    const ivWords = CryptoJS.enc.Hex.parse(iv);

    // Process each string
    return texts.map((text) => {
      const encrypted = CryptoJS.AES.encrypt(text, keyWords, {
        iv: ivWords,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      return encrypted.toString();
    });
  } catch {
    throw new Error("Failed to encrypt the provided strings");
  }
}

/**
 * Decrypts an array of encrypted strings
 */
export function decryptBatch(encryptedTexts: string[]): string[] {
  if (!Array.isArray(encryptedTexts)) {
    throw new Error("Input must be an array of encrypted strings");
  }

  try {
    const { key, iv } = getEncryptionParameters();
    const keyWords = CryptoJS.enc.Hex.parse(key);
    const ivWords = CryptoJS.enc.Hex.parse(iv);

    // Process each encrypted string
    return encryptedTexts.map((encryptedText) => {
      const decrypted = CryptoJS.AES.decrypt(encryptedText, keyWords, {
        iv: ivWords,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      return decrypted.toString(CryptoJS.enc.Utf8);
    });
  } catch {
    throw new Error("Failed to decrypt the provided strings");
  }
}
